import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { setChecklist, setView } from './onboardingSlice';
import { fetchChecklist, startDirectDebit } from './checklistApi';

const DirectDebitSetup: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { applicationId, brandConfig, checklist } = useSelector((s: RootState) => s.onboarding);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const done = checklist?.gocardless_dd_status === 'COMPLETED';

  useEffect(() => {
    if (!applicationId || done) return undefined;

    let cancelled = false;
    const pull = async () => {
      try {
        const row = await fetchChecklist(applicationId);
        if (cancelled) return;
        dispatch(setChecklist(row));
        if (row.gocardless_dd_status === 'COMPLETED') dispatch(setView('hub'));
      } catch (_) { /* keep polling */ }
    };

    pull();
    const id = window.setInterval(pull, 2000);
    const onFocus = () => { pull(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [applicationId, dispatch, done]);

  const open = async () => {
    if (!applicationId) {
      setError('Submit the onboarding form first.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { application, flowUrl } = await startDirectDebit(applicationId, brandConfig);
      dispatch(setChecklist(application));
      if (flowUrl) window.open(flowUrl, '_blank', 'noopener');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not open Direct Debit setup');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Button variant="text" onClick={() => dispatch(setView('hub'))} sx={{ mb: 1, px: 0 }}>← Back to overview</Button>
      <Typography sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700, fontSize: 28, mb: 1 }}>
        Set up Direct Debit
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        Set up Direct Debit for monthly payments using the secure GoCardless page.
        Fees are set out in the merchant conditions you accepted earlier. Applicable VAT may be added.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {done ? (
        <Alert severity="success">Direct Debit is set up.</Alert>
      ) : (
        <>
          <Button variant="contained" onClick={open} disabled={busy}>
            {busy ? 'Opening…' : 'Set up Direct Debit'}
          </Button>
          {checklist?.gocardless_dd_status === 'IN_PROGRESS' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Finish the GoCardless form. This page updates when the mandate is confirmed.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default DirectDebitSetup;
